import * as React from "react";
import Autosuggest, { SuggestionsFetchRequestedParams } from "react-autosuggest";
import { useImgI18N } from "services/ImgI18N";
import { useThemePart, Theme, Icon } from "imaginarity-react-ui";
import { GeneralPostSearchSC } from "./GeneralPostSearchSC";
import AutocompleteSearch from "components/AutocompleteSearch/AutocompleteSearch";
import * as _ from "lodash";
import { CMSController, FetchPostsFilter, FileEntrySearchFilter, PostController, WikiController } from "collaboration-service";

// Hilfsfunktion für Theme
const tp = (t: Theme) => ({
    mainBackground: t.colors.mainBackground,
    darkerGrey: t.colors.darkerGrey,
    accent: t.colors.accent,
});

export interface GlobalPostSearchSearchFieldProps {
    onSearch?: (val: string) => void;
    filter: FetchPostsFilter;
    feFilter?: FileEntrySearchFilter;
    suggestMethod: typeof CMSController.SuggestFiles | typeof CMSController.SuggestProjects | typeof WikiController.SuggestTerm | typeof PostController.SuggestPostTerm2;
    lng?: string;
}

const GlobalPostSearchSearchField = (p: GlobalPostSearchSearchFieldProps) => {
    const { onSearch, filter, suggestMethod, lng, feFilter } = p;
    const { t } = useImgI18N("general");
    const [search, setSearch] = React.useState<string>();
    const [suggestions, setSuggestions] = React.useState<string[]>([]);
    const { mainBackground, darkerGrey, accent } = useThemePart(tp);

    React.useEffect(() => {
        setSearch("");
        setSuggestions([]);
    }, [filter]);

    const onEnterPressed = React.useMemo(() => () => {
        if (onSearch)
            onSearch(search ?? "");
    }, [onSearch, search]);

    const onSuggestionSelected = React.useMemo(() => (e: any, data: { suggestion: string }) => {
        if (search) {
            const prefixEnd = search.lastIndexOf(" ");
            let prefix = "";
            if (prefixEnd >= 0)
                prefix = search.slice(0, prefixEnd) + " ";
            const s = `${prefix}${data.suggestion}`;
            if (onSearch)
                onSearch(s);
        }
    }, [onSearch, search]);

    const onSuggestionsFetchRequested = React.useMemo(() => _.debounce((request: SuggestionsFetchRequestedParams) => {
        if (request.reason === "input-changed" || request.reason === "input-focused") {
            let terms = request.value.split(" ");
            terms = _.filter(terms, t => t.length > 0);
            const lastTerm = _.last(terms);
            if (lastTerm) {
                if (lng)
                    suggestMethod({ term: lastTerm, text: lastTerm, filter, lng, feFilter, admin: false } as any,
                        suggestions => {
                            setSuggestions(suggestions);
                        });
                else
                    suggestMethod({ term: lastTerm, text: lastTerm, filter, feFilter, admin: false } as any,
                        suggestions => {
                            setSuggestions(suggestions);
                        });
            }
        }
    }, 250), [setSuggestions, filter, lng, suggestMethod, feFilter]);

    const searchChanged = React.useMemo(() => (event: React.FormEvent<any>, params: Autosuggest.ChangeEvent) => {
        setSearch(params.newValue);
    }, [setSearch]);

    const resetSearchStr = React.useMemo(() => () => {
        setSearch("");
        if (onSearch)
            onSearch("");
    }, [onSearch, setSearch]);

    const onSuggestionsClearRequested = React.useMemo(() => () => {
        setSuggestions([]);
    }, [setSuggestions]);

    const { input, inputFocused, suggestion, suggestionsContainer, suggestionHighlighted, suggestionsContainerOpen, suggestionsList } = React.useMemo(() => {
        return ({
            input: {
                backgroundColor: "#fff",
                color: "#313639",
                height: 40,
                border: `1px solid #cccccc`,
                outline: "none",
                width: "100%",
                padding: "0 10px 0 10px",
                transition: "all 0.2s ease-out",
            },
            inputFocused: {
                background: "#f5f4f7",
                backgroundColor: "#f5f4f7",
                color: "#000",
                transition: "all 0.2s ease-out",
                boxShadow: "none",
                outline: "0 none",
            },
            // to be continued !!!
            suggestionsContainer: {
                display: "none",
                width: "100%",
                background: "@veryLightGrey",
                backgroundColor: "@veryLightGrey",
                marginLeft: 0,
                boxShadow: "0 6px 8px -6px #000",
            } as React.CSSProperties,
            suggestionsContainerOpen: {
                background: "@veryLightGrey",
                backgroundColor: "@veryLightGrey",
                color: darkerGrey,
                display: "block",
                position: "absolute",
                top: 40,
                border: "1px solid #e4e3e3",
                fontSize: "1em",
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                zIndex: 3000
            } as React.CSSProperties,
            suggestionsList: {
                margin: 0,
                padding: 0,
                listStyleType: "none",
                paddingTop: 0,
                paddingBottom: 0
            } as React.CSSProperties,
            suggestion: {
                background: mainBackground,
                backgroundColor: mainBackground,
                paddingTop: 10,
                paddingBottom: 10,
                paddingLeft: 10,
                cursor: "pointer",
                transition: "all 0.2s ease-out",
            } as React.CSSProperties,
            suggestionHighlighted: {
                background: accent,
                backgroundColor: accent,
                color: mainBackground,
                borderLeft: ""
            } as React.CSSProperties,
        })
    }, [mainBackground, darkerGrey, accent]);

    return (
        <GeneralPostSearchSC.TSAuto suggested={suggestions.length > 0 ? suggestions.length + 1 : 0} >
            <AutocompleteSearch
                placeholder={t("search..")}
                onSuggestionSelected={onSuggestionSelected}
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                search={search ?? ""}
                searchChanged={searchChanged}
                suggestions={suggestions}
                theme={{
                    container: { position: "relative" },
                    input,
                    inputFocused,
                    suggestionsContainer,
                    suggestionsContainerOpen,
                    suggestionsList,
                    suggestion,
                    suggestionHighlighted
                }}
                onEnterPressed={onEnterPressed}
            />
            {search !== "" && search !== undefined &&
                <GeneralPostSearchSC.TSSearchDelete
                    onClick={search !== "" ? resetSearchStr : undefined}
                >
                    <Icon marginTop={9} name="times" size="20px" />
                </GeneralPostSearchSC.TSSearchDelete>
            }
        </GeneralPostSearchSC.TSAuto>
    );
}

export default GlobalPostSearchSearchField;
